import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Directive({
    selector: '[appPtBrNumber]',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PtBrNumberDirective),
            multi: true
        }
    ]
})
export class PtBrNumberDirective implements ControlValueAccessor {
    // Callback para informar o Angular sobre a mudança de valor.
    private onChange: (value: number | null) => void = () => { };
    // Callback para informar que o controle foi tocado.
    private onTouched: () => void = () => { };

    // Armazena o último valor válido (number ou null)
    private previousValidValue: number | null = null;

    constructor(private el: ElementRef<HTMLInputElement>) { }

    /**
     * Atualiza o input com o valor formatado (mesmo sem foco).
     */
    writeValue(value: number | null): void {
        this.previousValidValue = value;
        if (value !== null && !isNaN(value)) {
            this.el.nativeElement.value = this.formatNumber(value);
        } else {
            this.el.nativeElement.value = '';
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Durante a digitação, permite que o usuário apague e insira o valor.
     * Aqui não fazemos formatação “on the fly” para não atrapalhar a edição.
     */
    @HostListener('input', ['$event'])
    onInput(event: InputEvent): void {
        // Apenas capturamos o evento sem reformatar o valor.
        // Assim, o usuário pode digitar livremente (com dígitos e vírgula).
        // Não atualizamos o model ainda para não interferir na edição.
    }

    /**
     * Ao pressionar qualquer tecla, permite apenas números e a vírgula.
     */
    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const key = event.key;

        // Permite apenas números, vírgula, backspace e delete e arrows.
        if (!/[\d,]/.test(key) && key !== 'Backspace' && key !== 'Delete') {

            // Permite as setas para mover o cursor
            if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown') {
                return;
            }
            event.preventDefault();
        }
    }

    /**
     * Ao perder o foco, valida o valor digitado:
     * - Se for válido, formata o input e atualiza o model.
     * - Se for inválido, exibe mensagem e reverte para o último valor válido.
     */
    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
        const inputValue = this.el.nativeElement.value;

        // Se o usuário apagou tudo, consideramos como inválido (ou
        // caso o campo seja obrigatório, você pode tratar de outra forma)
        if (inputValue.trim() === '') {
            alert('Valor inválido. Retornando ao último valor válido.');
            this.revertValue();
            return;
        }

        // Remove os pontos de milhar e substitui a vírgula pelo ponto
        // para converter corretamente a string para número.
        const normalized = inputValue.replace(/\./g, '').replace(',', '.');
        const parsedValue = parseFloat(normalized);

        if (isNaN(parsedValue)) {
            alert('Valor inválido. Retornando ao último valor válido.');
            this.revertValue();
        } else {
            // Valor válido: atualiza o previousValidValue, formata o input e atualiza o model.
            this.previousValidValue = parsedValue;
            this.el.nativeElement.value = this.formatNumber(parsedValue);
            this.onChange(parsedValue);
        }
    }

    /**
     * Reverte o valor exibido para o último valor válido armazenado.
     */
    private revertValue(): void {
        if (this.previousValidValue !== null && !isNaN(this.previousValidValue)) {
            this.el.nativeElement.value = this.formatNumber(this.previousValidValue);
            this.onChange(this.previousValidValue);
        } else {
            this.el.nativeElement.value = '';
            this.onChange(null);
        }
    }

    /**
     * Formata o número de acordo com o padrão pt-BR (ex: 1.234,56)
     */
    private formatNumber(value: number): string {
        return value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
}